import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  Network, ArrowRight, Check, Sparkles, Shield, Globe, Building2,
  ChevronRight, Zap, Lock, BarChart3
} from 'lucide-react';

export default function LandingPage() {
  const navigate = useNavigate();

  const features = [
    {
      icon: Sparkles,
      title: 'AI-Powered Analysis',
      description: 'Get intelligent recommendations for optimal jurisdictions based on your industry and goals.'
    },
    {
      icon: Globe,
      title: 'Global Jurisdictions',
      description: 'Access insights for 50+ jurisdictions worldwide, from Delaware to Singapore.'
    },
    {
      icon: Shield,
      title: 'Asset Protection',
      description: 'Structure your entities for maximum asset protection and liability shielding.'
    },
    {
      icon: Building2,
      title: 'Structure Automation',
      description: 'Automatically generate optimal corporate structures with one click.'
    },
    {
      icon: Lock,
      title: 'Compliance Ready',
      description: 'Stay compliant with built-in governance rules and audit trails.'
    },
    {
      icon: BarChart3,
      title: 'Tax Optimization',
      description: 'Leverage legal tax strategies with proper corporate structuring.'
    }
  ];

  const benefits = [
    'Reduce setup time from weeks to minutes',
    'AI analyzes 50+ jurisdiction variables',
    'Human override on all AI decisions',
    'Full audit trail for compliance',
    'Secure, encrypted data storage'
  ];

  return (
    <div className="min-h-screen bg-[#020617]" data-testid="landing-page">
      {/* Navigation */}
      <nav className="border-b border-slate-800 bg-slate-950/80 backdrop-blur sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img 
              src="/corpcraft-logo.png" 
              alt="CorpCraft Logo" 
              className="h-10 w-auto object-contain"
            />
          </Link>
          <div className="flex items-center gap-4">
            <Link 
              to="/login" 
              className="text-slate-400 hover:text-white transition-colors"
              data-testid="nav-login"
            >
              Sign In
            </Link>
            <Button 
              onClick={() => navigate('/register')}
              className="gradient-accent hover:opacity-90"
              data-testid="nav-get-started"
            >
              Get Started
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 gradient-hero" />
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1764083267096-7351f4d0d913?crop=entropy&cs=srgb&fm=jpg&q=85')] bg-cover bg-center opacity-10" />
        
        <div className="relative max-w-7xl mx-auto px-6 py-24 md:py-32">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/30 text-blue-400 text-sm mb-8">
              <Sparkles className="w-4 h-4" />
              AI-Powered Corporate Structuring
            </div>
            
            <h1 className="text-5xl md:text-7xl font-bold font-['Outfit'] leading-tight mb-6">
              Build Your Corporate
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-400">
                Empire with AI
              </span>
            </h1>
            
            <p className="text-xl text-slate-400 mb-10 max-w-2xl leading-relaxed">
              CorpCraft automates the creation of optimal corporate structures. 
              Select your industry, and let AI recommend the best jurisdictions 
              for tax efficiency and asset protection.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                onClick={() => navigate('/register')}
                className="gradient-accent hover:opacity-90 h-14 px-8 text-lg"
                data-testid="hero-get-started"
              >
                Start Building Free
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button 
                variant="outline"
                className="h-14 px-8 text-lg border-slate-700 text-slate-300 hover:bg-slate-800"
                data-testid="hero-learn-more"
              >
                Learn More
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-24 border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold font-['Outfit'] mb-4">
              Everything You Need
            </h2>
            <p className="text-xl text-slate-400 max-w-2xl mx-auto">
              Powerful tools to structure, manage, and optimize your corporate entities
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, i) => (
              <div 
                key={i}
                className="stat-card group"
                style={{ animationDelay: `${i * 0.1}s` }}
              >
                <div className="w-14 h-14 rounded-xl bg-blue-500/10 flex items-center justify-center mb-5 group-hover:bg-blue-500/20 transition-colors">
                  <feature.icon className="w-7 h-7 text-blue-400" />
                </div>
                <h3 className="text-xl font-semibold font-['Outfit'] mb-3">{feature.title}</h3>
                <p className="text-slate-400 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold font-['Outfit'] mb-6">
                Why Choose CorpCraft?
              </h2>
              <p className="text-xl text-slate-400 mb-10">
                We combine advanced AI with deep legal expertise to deliver 
                corporate structures that protect and optimize your business.
              </p>
              <div className="space-y-4">
                {benefits.map((benefit, i) => (
                  <div key={i} className="flex items-center gap-4">
                    <div className="w-6 h-6 rounded-full bg-emerald-500/20 flex items-center justify-center shrink-0">
                      <Check className="w-4 h-4 text-emerald-400" />
                    </div>
                    <span className="text-slate-300">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="aspect-square rounded-2xl bg-gradient-to-br from-slate-800 to-slate-900 border border-slate-700 p-8 flex items-center justify-center">
                <div className="text-center">
                  <div className="w-24 h-24 rounded-2xl gradient-accent mx-auto mb-6 flex items-center justify-center">
                    <Network className="w-12 h-12 text-white" />
                  </div>
                  <p className="text-2xl font-bold font-['Outfit'] mb-2">500+</p>
                  <p className="text-slate-400">Structures Generated</p>
                </div>
              </div>
              <div className="absolute -bottom-6 -right-6 w-32 h-32 rounded-xl bg-blue-500/20 blur-3xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Press/Media Rolling Tape */}
      <section className="border-t border-b border-slate-800 bg-slate-950/60 overflow-hidden py-6" data-testid="press-tape">
        <div className="flex items-center gap-3 px-6 mb-3 max-w-7xl mx-auto">
          <div className="h-px bg-slate-700 flex-1"></div>
          <span className="text-xs uppercase tracking-[0.25em] text-slate-500">As Seen On</span>
          <div className="h-px bg-slate-700 flex-1"></div>
        </div>
        <div className="relative">
          <div className="flex gap-16 whitespace-nowrap animate-marquee">
            {Array.from({ length: 3 }).flatMap((_, groupIdx) => [
              'CNBC MAD MONEY',
              'BLOOMBERG REPORT',
              'WALL STREET JOURNAL',
              "INVESTOR'S BUSINESS DAILY",
            ].map((label, i) => (
              <span
                key={`${groupIdx}-${i}`}
                className="text-slate-300 font-semibold tracking-widest text-lg md:text-xl font-['Outfit'] opacity-80 hover:opacity-100 transition"
              >
                {label}
                <span className="mx-10 text-slate-600">•</span>
              </span>
            )))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 border-t border-slate-800">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold font-['Outfit'] mb-6">
            Ready to Optimize Your Structure?
          </h2>
          <p className="text-xl text-slate-400 mb-10">
            Join thousands of businesses using CorpCraft to build optimal corporate structures.
          </p>
          <Button 
            onClick={() => navigate('/register')}
            className="gradient-accent hover:opacity-90 h-14 px-10 text-lg"
            data-testid="cta-get-started"
          >
            Get Started Free
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-800 py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <img 
                src="/corpcraft-logo.png" 
                alt="CorpCraft Logo" 
                className="h-10 w-auto object-contain"
              />
            </div>
            <p className="text-slate-500 text-sm">
              © 2026 CorpCraft. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
